import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Package, BookOpen, FileText, Loader2, CornerDownLeft } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { useAuth } from "../context/AuthContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_META = {
  order: { icon: Package, color: "text-blue-600", label: "Order" },
  course: { icon: BookOpen, color: "text-emerald-600", label: "Course" },
  inquiry: { icon: FileText, color: "text-purple-600", label: "Inquiry" },
};

const GlobalSearch = ({ open, onClose, onSelectSection }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();

  // Focus input when opened
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      setQuery("");
      setResults([]);
      setActiveIdx(0);
    }
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/admin/search?q=${encodeURIComponent(query)}`, { headers: getAuthHeader() });
        setResults(res.data.results || []);
        setActiveIdx(0);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [query, getAuthHeader]);

  const handleSelect = useCallback((r) => {
    onClose();
    if (r.type === "course") {
      navigate(r.url);
      return;
    }
    // Orders + Inquiries route within admin dashboard
    if (r.type === "order") onSelectSection?.("orders");
    else if (r.type === "inquiry") onSelectSection?.("inquiries");
  }, [navigate, onClose, onSelectSection]);

  const handleKeyDown = (ev) => {
    if (ev.key === "ArrowDown") {
      ev.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, results.length - 1));
    } else if (ev.key === "ArrowUp") {
      ev.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (ev.key === "Enter" && results[activeIdx]) {
      handleSelect(results[activeIdx]);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="p-0 max-w-2xl gap-0 top-[20%] translate-y-0" data-testid="global-search-dialog">
        <VisuallyHidden asChild><DialogTitle>Global search</DialogTitle></VisuallyHidden>
        <VisuallyHidden asChild><DialogDescription>Search orders, courses, and inquiries</DialogDescription></VisuallyHidden>
        <div className="flex items-center gap-3 px-4 py-3 border-b">
          <Search className="h-5 w-5 text-slate-400" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search orders, courses, inquiries..."
            className="flex-1 outline-none text-base placeholder:text-slate-400"
            data-testid="global-search-input"
          />
          {loading && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
          <kbd className="hidden sm:inline-flex items-center rounded border bg-slate-100 px-1.5 py-0.5 text-[10px] font-mono text-slate-500">ESC</kbd>
        </div>
        <div className="max-h-96 overflow-y-auto py-2" data-testid="global-search-results">
          {!query.trim() && (
            <div className="px-4 py-8 text-center text-sm text-slate-400">
              Type to search orders, courses, customers, or inquiries.
            </div>
          )}
          {query.trim() && !loading && results.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-slate-400">No results for "{query}"</div>
          )}
          {results.map((r, i) => {
            const meta = TYPE_META[r.type] || TYPE_META.order;
            const Icon = meta.icon;
            const active = i === activeIdx;
            return (
              <button
                key={`${r.type}-${r.id}-${i}`}
                type="button"
                onClick={() => handleSelect(r)}
                onMouseEnter={() => setActiveIdx(i)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                  active ? "bg-brand/5" : "hover:bg-slate-50"
                }`}
                data-testid={`search-result-${i}`}
              >
                <div className={`p-1.5 rounded ${meta.color} bg-slate-100`}>
                  <Icon className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{r.title}</p>
                  <p className="text-xs text-slate-500 truncate">{r.subtitle}</p>
                </div>
                <span className="text-[10px] uppercase tracking-wider text-slate-400">{meta.label}</span>
                {active && <CornerDownLeft className="h-3.5 w-3.5 text-slate-400" />}
              </button>
            );
          })}
        </div>
        <div className="border-t px-3 py-2 text-[11px] text-slate-500 flex items-center justify-between">
          <span className="flex items-center gap-3">
            <span className="flex items-center gap-1"><kbd className="rounded border bg-slate-100 px-1.5">↑↓</kbd> navigate</span>
            <span className="flex items-center gap-1"><kbd className="rounded border bg-slate-100 px-1.5">↵</kbd> open</span>
          </span>
          <span>Press <kbd className="rounded border bg-slate-100 px-1.5">/</kbd> or <kbd className="rounded border bg-slate-100 px-1.5">⌘K</kbd> to open</span>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GlobalSearch;
